/* @flow */
import type { Action } from "../action";

// Настройки, которые мы храним
export type TradeSettings = {
  currentBid: number,
  currentInterval: number
};

// Настройки UI, индикаторы на графике
export type MovingAverageConfig = {
  type: "MovingAverage",
  isOscillator: false,
  algorithm: "SMA" | "EMA",
  period: number,
  shift: number,
  rejection: number,
  strokeWidth: number,
  color: string
};

export type RSIConfig = {
  type: "RSI",
  isOscillator: true,
  period: number,
  oversold: number,
  overbought: number,
  shift: number,
  rejection: number,
  strokeWidth: number,
  color: string
};

export type ATRConfig = {
  type: "ATR",
  isOscillator: true,
  period: number,
  strokeWidth: number,
  color: string
};

export type MACDConfig = {
  type: "MACD",
  isOscillator: true,
  fastPeriod: number,
  slowPeriod: number,
  period: number,
  strokeWidth: number,
  macdWidth: number,
  color: string,
  macdColor: string,
  likeAPro: boolean
};

export type ROCConfig = {
  type: "ROC",
  isOscillator: true,
  period: number,
  strokeWidth: number,
  color: string
};

export type StochConfig = {
  type: "Stoch",
  isOscillator: true,
  periodK: number,
  periodD: number,
  algorithm: "EMA" | "SMA",
  slowing: number,
  strokeWidth: number,
  colorK: string,
  colorD: string,
  overbought: number,
  oversold: number
};

export type ParabolicSARConfig = {
  type: "ParabolicSAR",
  isOscillator: false,
  accelerationFactor: number,
  accelerationMaxValue: number,
  accelerationStep: number,
  shift: number,
  size: number,
  color: string
};

export type AlligatorConfig = {
  type: "Alligator",
  isOscillator: false,
  jaws: MovingAverageConfig,
  teeths: MovingAverageConfig,
  lips: MovingAverageConfig
};

export type BollingerLinesConfig = {
  type: "BollingerLines",
  isOscillator: false,
  upper: MovingAverageConfig,
  middle: MovingAverageConfig,
  lower: MovingAverageConfig
};

export type IndicatorConfig =
  | MovingAverageConfig
  | ATRConfig
  | RSIConfig
  | MACDConfig
  | ROCConfig
  | StochConfig
  | AlligatorConfig
  | BollingerLinesConfig;

export type UISettings = {
  lineColor: string,
  lineThickness: number,
  interval: number,
  language: string,
  sound: boolean,
  timeSpan: number,
  indicators: Array<IndicatorConfig>,
  chartType: "line" | "candlestick" | "bar",
  sentiments: boolean,
  theme: string,
  mode?: "binary" | "tournament", // Добавленного после дела поля может не быть в клиентском localStorage
  showAboutTournaments: boolean
};

export type State = TradeSettings & UISettings;

const initialState = {
  currentBid: 1000,
  currentInterval: null,
  lineColor: "#00cbfe",
  lineThickness: 2,
  interval: 10,
  language: "en",
  sound: false,
  timeSpan: 120 * 1000,
  indicators: [],
  graphicalTools: {},
  chartType: "line",
  sentiments: true,
  theme:
    process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ? "light" : "default",
  tournamentsPerPage: 10,
  mode: "binary",
  showAboutTournaments: true
};

function toggleIndicator(state, index) {
  return {
    ...state,
    indicators: state.indicators.map((indicator, id) => {
      if (id === index) {
        return {
          ...indicator,
          // Use direct assertion to false because enabled can be undefined
          // in which case the toggle would be displayed as Enabled
          enabled: indicator.enabled === false ? true : false
        };
      }

      return indicator;
    })
  };
}

function removeIndicator(state, index) {
  return {
    ...state,
    indicators: state.indicators.filter((_, i) => i !== index)
  };
}

function updateSettings(state, payload) {
  return { ...state, ...payload };
}

function updateInterval(state, payload) {
  return {
    ...state,
    currentInterval: state.currentInterval || payload.defaultInterval
  };
}

function receiveSocketMessage(state, message) {
  if (message.op === "tournament-status-change" && message.status === "ended") {
    return {
      ...state,
      mode: "binary"
    };
  }

  return state;
}

function pushGraphicalTool(state, payload) {
  const newState = { ...state };

  if (!newState.graphicalTools) {
    newState.graphicalTools = {};
  }

  if (!newState.graphicalTools[payload.stock]) {
    newState.graphicalTools[payload.stock] = {
      enabled: true,
      tools: []
    };
  }

  const newStockTools = [...newState.graphicalTools[payload.stock].tools];

  newStockTools.push(payload);

  newState.graphicalTools[payload.stock].tools = newStockTools;
  // Turn tools on so user can see the result of their actions
  newState.graphicalTools[payload.stock].enabled = true;

  return newState;
}

function updateGraphicalTool(state, payload) {
  const newState = { ...state };
  const newStockTools = [...newState.graphicalTools[payload.stock].tools];

  // Reset tool's visibility so user can see the result of their actions
  const newPayload = {
    ...payload,
    enabled:
      payload.enabled === newStockTools[payload.index].enabled
        ? true
        : payload.enabled
  };
  newStockTools[payload.index] = Object.assign(
    {},
    newStockTools[payload.index],
    newPayload
  );

  newState.graphicalTools[payload.stock].tools = newStockTools;
  // Turn tools on so user can see the result of their actions
  newState.graphicalTools[payload.stock].enabled = true;

  return newState;
}

function removeGraphicalTool(state, payload) {
  const newState = { ...state };
  const newStockTools = [...newState.graphicalTools[payload.stock].tools];
  newStockTools.splice(payload.index, 1);

  newState.graphicalTools[payload.stock].tools = newStockTools;

  return newState;
}

function purgeGraphicalTools(state, payload) {
  const newState = { ...state };

  if (!newState.graphicalTools) {
    newState.graphicalTools = {};
  }

  if (!newState.graphicalTools[payload.activeStock]) {
    return state;
  }

  newState.graphicalTools[payload.activeStock].tools = [];

  delete newState.graphicalTools[payload.activeStock];

  return newState;
}

function updateGraphicalToolsVisibility(state, payload) {
  const newState = { ...state };

  newState.graphicalTools[payload.stock].enabled = payload.value;

  return newState;
}

function settings(state: State = initialState, action: Action): State {
  switch (action.type) {
    case "DROP_SETTINGS":
      return {
        ...initialState,
        theme:
          process.env.REACT_APP_DEPLOY_TARGET === "irontrade" &&
          action.currency !== "THB"
            ? "light"
            : "default"
      };
    case "RECEIVE_SETTINGS":
      return action.payload;
    case "UPDATE_SETTINGS":
      return updateSettings(state, action.payload);
    case "PUSH_INDICATOR":
      return {
        ...state,
        indicators: [...state.indicators, { ...action.payload, enabled: true }]
      };
    case "UPDATE_INDICATOR":
      return {
        ...state,
        indicators: state.indicators.map((indicator, id) =>
          action.payload.id === id ? action.payload : indicator
        )
      };
    case "TOGGLE_INDICATOR":
      return toggleIndicator(state, action.payload);
    case "REMOVE_INDICATOR":
      return removeIndicator(state, action.payload);
    case "REMOVE_ALL_INDICATORS":
      return {
        ...state,
        indicators: []
      };
    case "PUSH_GRAPHICAL_TOOL":
      return pushGraphicalTool(state, action.payload);
    case "UPDATE_GRAPHICAL_TOOL":
      return updateGraphicalTool(state, action.payload);
    case "REMOVE_GRAPHICAL_TOOL":
      return removeGraphicalTool(state, action.payload);
    case "SET_GRAPHICAL_TOOLS_VISIBILITY":
      return updateGraphicalToolsVisibility(state, action.payload);
    case "SET_ACTIVE_STOCK":
    case "CLEAR_GRAPHICAL_TOOLS":
      return purgeGraphicalTools(state, action.payload);
    case "RECEIVE_TRADE_SETTINGS":
      return updateInterval(state, action.payload);
    case "ENGAGE_TOURNAMENT_MODE":
      return {
        ...state,
        mode: "tournament"
      };
    case "ENGAGE_BINARY_MODE":
      return {
        ...state,
        mode: "binary"
      };
    case "SWITCH_ACCOUNTS":
      return {
        ...state,
        mode: "binary"
      };
    case "RECEIVE_SOCKET_MESSAGE":
      return receiveSocketMessage(state, action.payload);
    default:
      return state;
  }
}

export default settings;



// WEBPACK FOOTER //
// ./src/redux/reducer/settings.js